/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing. The function also has a property 'clear' 
 * that is a function which will clear the timer to prevent previously scheduled executions. 
 *
 * @source underscore.js
 * @see http://unscriptable.com/2009/03/20/debouncing-javascript-methods/
 * @param {Function} function to wrap
 * @param {Number} timeout in ms (`100`)
 * @param {Boolean} whether to execute at the beginning (`false`)
 * @api public
 */
export function debounce(func, wait, immediate) {
	let timeout, args, context, timestamp, result
	if (null == wait) wait = 100

	function later() {
		const last = Date.now() - timestamp

		if (last < wait && last >= 0) {
			timeout = setTimeout(later, wait - last)
		} else {
			timeout = null
			if (!immediate) {
				result = func.apply(context, args)
				context = args = null
			}
		}
	}

	let debounced = function () {
		context = this
		args = arguments
		timestamp = Date.now()
		var callNow = immediate && !timeout
		if (!timeout) timeout = setTimeout(later, wait)
		if (callNow) {
			result = func.apply(context, args)
			context = args = null
		}

		return result
	}

	debounced.clear = function () {
		if (timeout) {
			clearTimeout(timeout)
			timeout = null
		}
	}

	debounced.flush = function () {
		if (timeout) {
			result = func.apply(context, args)
			context = args = null

			clearTimeout(timeout)
			timeout = null
		}
	}

	return debounced
}

export const lerp = (a, b, n) => (1 - n) * a + n * b
export const map = (x, a, b, c, d) => (x - a) * (d - c) / (b - a) + c
export const clamp = (num, min, max) => num <= min ? min : num >= max ? max : num

export const delay = (n) => {
	n = n || 2000
	return new Promise((resolve) => {
		setTimeout(() => {
			resolve()
		}, n)
	})
}

export const get = (obj, path, defValue) => {
	// replacement for lodash get() https://youmightnotneed.com/lodash/
	// If path is not defined or it has false value
	if (!path) return undefined
	// Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
	// Regex explained: https://regexr.com/58j0k
	const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
	// Find value
	const result = pathArray.reduce(
		(prevObj, key) => prevObj && prevObj[key],
		obj
	)
	// If found value is undefined return default value; otherwise return the value
	return result === undefined ? defValue : result
}

export const stringToHtml = (string) => {
	return string.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
}

export const stripHtml = (strToSanitize) => {
	if (process.server) return strToSanitize.replace(/(<([^>]+)>)/ig, '')

	let myHTML = new DOMParser().parseFromString(strToSanitize, 'text/html')

	return myHTML.body.textContent
}

export const rot13 = (message) => {
	//https://hellodevworld.com/365-days-of-coding/rot13-cipher-javascript-solution
	return message.replace(/[a-z]/gi, letter => String.fromCharCode(letter.charCodeAt(0) + (letter.toLowerCase() <= 'm' ? 13 : -13)))
}

export const isMobileUserAgent = () => {
	let check = false
	// regex from http://detectmobilebrowsers.com/

	if (typeof window !== 'undefined') {
		(function (a) {
			// Simplified mobile UA detection to avoid brittle/over-escaped regex
			const mobileRe = /android|bb\d+|meego|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|iphone|ipod|kindle|lge |maemo|midp|mmp|mobile|mobile.+firefox|netfront|opera mini|palm|phone|pocket|psp|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce/i
			const prefixRe = /^(1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|nokia|samsung)/i
			if (mobileRe.test(a) || prefixRe.test((a || '').substr(0, 4))) check = true
		})(navigator.userAgent || navigator.vendor || window.opera)

		// (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true })(navigator.userAgent || navigator.vendor || window.opera)

		// if (window.innerWidth < 660) { check = true }
	}

	return check
}

export const isEmpty = (value) => {
	// checks if the given value is empty. It first checks if the value is null or undefined. If it is, the function returns true. If the value is an array, a string, or an array - like object(i.e., it has a splice method), function checks if its length is 0. If it is, function returns true. Otherwise, function checks if the value is an object with no own enumerable string keyed properties. If it is, function returns true. Otherwise,  returns false.
	if (value == null) {
		return true
	}
	if (Array.isArray(value) || typeof value === 'string' || typeof value.splice === 'function') {
		return !value.length
	}
	for (const key in value) {
		if (Object.prototype.hasOwnProperty.call(value, key)) {
			return false
		}
	}
	return true
}

export const isAbsoluteLink = (url) => {
	if (!url) { return true }
	const exp = /^(?:[a-z]+:)?\/\//i

	return exp.test(url) ? true : null

	// exp.test('http://example.com'); // true - regular http absolute URL
	// exp.test('HTTP://EXAMPLE.COM'); // true - HTTP upper-case absolute URL
	// exp.test('https://www.exmaple.com'); // true - secure http absolute URL
	// exp.test('ftp://example.com/file.txt'); // true - file transfer absolute URL
	// exp.test('//cdn.example.com/lib.js'); // true - protocol-relative absolute URL
	// exp.test('/myfolder/test.txt'); // false - relative URL
	// exp.test('test'); // false - also relative URL
}

export const convertToString = (input) => {
	// Helper function to convert anything to string
	if (input === null || input === undefined) return ''

	if (typeof input === 'string') return removeEmojis(input)
	if (typeof input === 'number' || typeof input === 'boolean') return String(input)
	if (typeof input === 'object') {
		try {
			return JSON.stringify(input, (key, value) => {
				if (typeof value === 'string') {
					return removeEmojis(value)
				}

				return value
			})
		} catch {
			return Object.prototype.toString.call(input)
		}
	}

	return Object.prototype.toString.call(input)
}

const removeEmojis = (str) => {
	// Helper function to remove emojis
	return str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/gu, '')
}

export const toSentenceCase = (input) => {
	const str = convertToString(input).trim() // remove leading and trailing spaces
	if (str.length === 0) return ''

	let sentence = str
		.replace(/([A-Z])/g, ' $1')  // Add space before uppercase letters
		.replace(/([a-z])([A-Z])/g, '$1 $2')  // Handle cases like "XMLHttpRequest"
		.replace(/([a-zA-Z])([0-9])/g, '$1 $2')  // Separate letters and numbers
		.replace(/([0-9])([a-zA-Z])/g, '$1 $2')  // Separate numbers and letters
		.trim()  // Remove leading/trailing spaces
		.toLowerCase()

	// Capitalize the first letter
	sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)

	return sentence
}

export const toKebabCase = (input) => {
	const str = convertToString(input).trim() // remove leading and trailing spaces
	if (str.length === 0) return ''

	return str
		.replace(/\s+/g, '-') // Replace spaces with hyphens
		.replace(/([a-z])([A-Z])/g, '$1-$2') // Add hyphen between lower and upper case letters
		.replace(/[_\s]+/g, '-') // Replace underscores and any remaining spaces with hyphens
		.replace(/[^\w-]+/g, '') // Remove any non-word characters except hyphens
		.replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
		.toLowerCase() // Convert the whole string to lowercase
}

export const toCamelCase = (input) => {
	const str = convertToString(input).trim()
	if (str.length === 0) return ''

	const isUpperCase = (char) => char === char.toUpperCase() && char !== char.toLowerCase() // Helper function to check if a character is uppercase
	const isLowerCase = (char) => char === char.toLowerCase() && char !== char.toUpperCase() // Helper function to check if a character is lowercase

	let result = ''
	let capitalizeNext = false

	for (let i = 0; i < str.length; i++) {
		const currentChar = str[i]

		if (currentChar === '-' || currentChar === '_') {
			capitalizeNext = true // If current character is a dash or underscore, set flag to capitalize next character
		} else if (capitalizeNext) {
			result += currentChar.toUpperCase() // If flag is set, capitalize current character and reset flag
			capitalizeNext = false
		} else if (i === 0) {
			// Always lowercase the first character
			result += currentChar.toLowerCase()
		} else if (isUpperCase(currentChar)) {
			// Handle uppercase characters
			if (i > 0 && isLowerCase(str[i - 1])) {
				// If previous character was lowercase, keep current character uppercase
				// This maintains the uppercase in cases like "userId" or "XMLHttpRequest"
				result += currentChar
			} else if (i + 1 < str.length && isLowerCase(str[i + 1])) {
				// If next character is lowercase, keep current character uppercase
				// This maintains the start of a new word in cases like "ID" in "userID"
				result += currentChar
			} else {
				// Otherwise, convert to lowercase
				// This handles cases of consecutive uppercase like "XML" in "XMLHttpRequest"
				result += currentChar.toLowerCase()
			}
		} else {
			// For all other cases, keep the character as is
			result += currentChar
		}
	}

	return result
}

export function splitMimeFormat(mimeType) {
	if (typeof mimeType !== 'string') {
		// eslint-disable-next-line
		console.error("getMimeType: Invalid input: MIME type must be a string.")

		return null
	}

	const parts = mimeType.split('/')

	if (parts.length !== 2) {
		// eslint-disable-next-line
		console.error("getMimeType: Invalid MIME type format. Expected format 'type/subtype'; recieved:", mimeType)

		return null
	}

	const [type, subtype] = parts

	if (!type || !subtype) {
		// eslint-disable-next-line
		console.error("getMimeType: Invalid MIME type: Type or subtype is missing.")

		return null
	}

	return subtype
}

export function srcFormats(mime) {
	const defaults = ['avif', 'webp']

	const imageFormat = splitMimeFormat(mime)

	if (!imageFormat) return defaults

	const formatsArray = [...defaults, imageFormat]

	return formatsArray.join(',')
}

export function groupsByN(data, numColumns = 2) {
	// if (data.length >= 8) numberOfColumns = 2 // because 7 is the max number of ingredients in a single column for YesYum
	let numberOfColumns = numColumns

	const n = Math.ceil(data.length / numberOfColumns)
	const result = []

	for (let i = 0; i < data.length; i += n) result.push(data.slice(i, i + n))

	return result
}

/**
 * Calculate image sizes fallback width based on viewport width percentage and pixel density
 * @param {string|number} vw - Viewport width as string (e.g., '100vw', '50vw') or number (e.g., 100, 50)
 * @param {number} breakpointWidth - Base breakpoint width in pixels (default: 320)
 * @returns {number} - Image width in pixels (breakpointWidth * vw% * pixelRatio)
 */
export const getFallbackWidth = (vw, breakpointWidth = 768) => {
	// SSR-safe check
	if (typeof window === 'undefined') {
		const vwValue = typeof vw === 'string' ? parseFloat(vw.replace('vw', '')) : vw
		const percentage = vwValue / 100
		return Math.ceil(breakpointWidth * percentage * 2) // Assume 2x for SSR
	}

	const vwValue = typeof vw === 'string' ? parseFloat(vw.replace('vw', '')) : vw
	const percentage = vwValue / 100
	const ratio = window.devicePixelRatio || 1
	const width = breakpointWidth * percentage * ratio

	return Math.ceil(width)
}